// OpenAPI 3.0 格式适配器（独立于 Postman 适配器）
// 参考: https://spec.openapis.org/oas/v3.0.3

/**
 * 将内部项目数据转换为 OpenAPI 3.0 文档
 * @param {Array} projects 内部项目树
 * @param {Object} options { name, version, description }
 */
export function toOpenApiDocument(projects, options = {}) {
  const paths = {}
  const tags = new Set()

  // 遍历项目，收集所有 API
  const collectApis = (node, tag) => {
    if (!node) return
    if (node.type === 'api' || node.method) {
      addApiToPaths(node, paths, tags, tag)
    }
    if (node.children) {
      node.children.forEach(child => collectApis(child, tag))
    }
  }

  // 收集并合并各项目携带的 OpenAPI components（导入时保留的可复用定义）
  const components = {}
  projects.forEach(project => {
    collectApis(project, project.name)
    if (project && project.openapiComponents && typeof project.openapiComponents === 'object') {
      mergeComponents(components, project.openapiComponents)
    }
  })

  const doc = {
    openapi: '3.0.3',
    info: {
      title: options.name || 'DAPI Collection',
      version: options.version || '1.0.0',
      description: options.description || 'Exported from DAPI'
    },
    paths: paths
  }

  if (tags.size > 0) {
    doc.tags = Array.from(tags).map(name => ({ name, description: `分组: ${name}` }))
  }

  if (Object.keys(components).length > 0) {
    doc.components = components
  }

  return doc
}

// 深度合并 OpenAPI components 对象（schemas / responses / parameters ... 一层为命名空间，二层为命名定义）
function mergeComponents(target, source) {
  if (!target || !source || typeof source !== 'object') return
  Object.keys(source).forEach(key => {
    const srcVal = source[key]
    if (srcVal && typeof srcVal === 'object' && !Array.isArray(srcVal)) {
      if (!target[key] || typeof target[key] !== 'object') {
        target[key] = {}
      }
      Object.assign(target[key], srcVal)
    } else {
      target[key] = srcVal
    }
  })
}

// 将单个 API 加入 paths
function addApiToPaths(api, paths, tags, tag) {
  // 跳过 WebSocket / STOMP 等非 HTTP 方法
  const method = (api.method || 'GET').toLowerCase()
  const httpMethods = ['get', 'post', 'put', 'delete', 'patch', 'head', 'options']
  if (!httpMethods.includes(method)) {
    return
  }

  const { path } = parseApiUrl(api.url)
  if (!path) return

  if (!paths[path]) {
    paths[path] = {}
  }

  // 同 path + 同 method 会被覆盖（OpenAPI 要求唯一）
  const operation = {
    summary: api.name || '',
    description: api.description || '',
    parameters: buildParameters(api),
    responses: buildResponses(api)
  }

  if (tag) {
    operation.tags = [tag]
    tags.add(tag)
  }

  const requestBody = buildRequestBody(api.body)
  if (requestBody) {
    operation.requestBody = requestBody
  }

  paths[path][method] = operation
}

// 解析 API URL，提取 path 部分
function parseApiUrl(url) {
  if (!url) return { path: '/' }

  let workUrl = url
  // 移除 query string
  const qIdx = workUrl.indexOf('?')
  if (qIdx >= 0) {
    workUrl = workUrl.substring(0, qIdx)
  }

  let path = workUrl
  // 提取 path（如果是完整 URL）
  const protocolMatch = workUrl.match(/^https?:\/\/[^/]+(.*)$/i)
  if (protocolMatch) {
    path = protocolMatch[1] || '/'
  } else {
    // 处理 {{host}} 类环境变量前缀
    const envHostMatch = workUrl.match(/^\{\{[^}]+\}\}(.*)$/)
    if (envHostMatch) {
      path = envHostMatch[1] || '/'
    }
  }

  if (!path) path = '/'
  if (!path.startsWith('/')) path = '/' + path

  return { path }
}

// 构建参数列表（query params + headers）
function buildParameters(api) {
  const parameters = []

  // query 参数
  if (Array.isArray(api.params)) {
    api.params.forEach(p => {
      if (p.key) {
        parameters.push({
          name: p.key,
          in: 'query',
          required: p.required || false,
          schema: { type: mapType(p.type) },
          description: p.description || ''
        })
      }
    })
  }

  // header 参数（排除标准头）
  if (Array.isArray(api.headers)) {
    api.headers.forEach(h => {
      if (h.key && !isStandardHeader(h.key)) {
        parameters.push({
          name: h.key,
          in: 'header',
          required: false,
          schema: { type: 'string' },
          description: h.description || ''
        })
      }
    })
  }

  return parameters
}

// 内部类型 → OpenAPI 类型
function mapType(type) {
  const typeMap = {
    string: 'string',
    number: 'number',
    integer: 'integer',
    boolean: 'boolean',
    array: 'array',
    object: 'object'
  }
  return typeMap[type] || 'string'
}

// 判断是否为不应作为参数的标准头
function isStandardHeader(key) {
  const std = [
    'Content-Type', 'Accept', 'Authorization', 'Host',
    'User-Agent', 'Cookie', 'Connection', 'Content-Length'
  ]
  return std.some(s => s.toLowerCase() === (key || '').toLowerCase())
}

// 构建请求体
function buildRequestBody(body) {
  if (!body || body.type === 'none') return null

  switch (body.type) {
    case 'json': {
      const mediaType = { schema: { type: 'object' } }
      if (body.content) {
        try {
          mediaType.example = JSON.parse(body.content)
        } catch {
          mediaType.example = body.content
        }
      }
      return {
        description: 'JSON 请求体',
        required: true,
        content: { 'application/json': mediaType }
      }
    }
    case 'form': {
      const mediaType = {
        schema: {
          type: 'object',
          properties: buildFormProperties(body.formData)
        }
      }
      return {
        description: '表单数据',
        required: true,
        content: { 'multipart/form-data': mediaType }
      }
    }
    case 'x-www-form-urlencoded': {
      const mediaType = {
        schema: {
          type: 'object',
          properties: buildFormProperties(body.formData)
        }
      }
      return {
        description: 'URL 编码表单数据',
        required: true,
        content: { 'application/x-www-form-urlencoded': mediaType }
      }
    }
    case 'raw': {
      if (!body.content) return null
      const mediaType = { schema: { type: 'string' }, example: body.content }
      return {
        description: '原始文本',
        required: true,
        content: { 'text/plain': mediaType }
      }
    }
    case 'binary': {
      return {
        description: '二进制数据',
        required: true,
        content: {
          'application/octet-stream': {
            schema: { type: 'string', format: 'binary' }
          }
        }
      }
    }
    default:
      return null
  }
}

// 构建表单属性
function buildFormProperties(formData) {
  const properties = {}
  if (Array.isArray(formData)) {
    formData.forEach(item => {
      if (item.key) {
        const prop = {
          type: 'string',
          description: item.description || ''
        }
        if (item.type === 'file') {
          prop.format = 'binary'
        }
        if (item.value) {
          prop.example = item.value
        }
        properties[item.key] = prop
      }
    })
  }
  return properties
}

// 构建响应
function buildResponses(api) {
  const responses = {}
  const response = {
    description: (api.responseBody && api.responseBody.content) ? '成功响应' : '默认响应'
  }

  // 响应 headers
  if (Array.isArray(api.responseHeaders) && api.responseHeaders.length > 0) {
    response.headers = {}
    api.responseHeaders.forEach(h => {
      if (h.key) {
        response.headers[h.key] = {
          schema: { type: 'string' },
          description: h.description || ''
        }
        if (h.value) {
          response.headers[h.key].example = h.value
        }
      }
    })
  }

  // 响应内容
  if (api.responseBody && api.responseBody.type !== 'none' && api.responseBody.content) {
    response.content = {}
    const responseType = api.responseBody.type
    const content = api.responseBody.content

    if (responseType === 'json') {
      let example = content
      try {
        example = JSON.parse(content)
      } catch {
        // 保留原始字符串
      }
      response.content['application/json'] = {
        schema: { type: 'object' },
        example: example
      }
    } else if (responseType === 'xml') {
      response.content['application/xml'] = {
        schema: { type: 'string' },
        example: content
      }
    } else {
      response.content['text/plain'] = {
        schema: { type: 'string' },
        example: content
      }
    }
  }

  // 响应参数作为 schema
  if (Array.isArray(api.responseParams) && api.responseParams.length > 0) {
    const properties = {}
    api.responseParams.forEach(p => {
      if (p.key) {
        properties[p.key] = {
          type: mapType(p.type),
          description: p.description || ''
        }
        if (p.value) {
          properties[p.key].example = p.value
        }
      }
    })
    if (!response.content) response.content = {}
    if (!response.content['application/json']) {
      response.content['application/json'] = {
        schema: { type: 'object', properties }
      }
    }
  }

  responses['200'] = response
  return responses
}

/**
 * 将 OpenAPI 3.0 文档转换为内部项目数据
 */
export function fromOpenApiDocument(doc) {
  if (!doc || !doc.paths) return []

  const projectName = (doc.info && doc.info.title) || '导入的 OpenAPI'
  const projectDescription = (doc.info && doc.info.description) || ''
  const baseUrl = (doc.servers && doc.servers[0] && doc.servers[0].url) || ''

  const children = []

  Object.keys(doc.paths).forEach(path => {
    const pathItem = doc.paths[path]
    if (!pathItem) return

    ;['get', 'post', 'put', 'delete', 'patch', 'head', 'options'].forEach(method => {
      const operation = pathItem[method]
      if (!operation) return
      children.push(convertOperationToApi(path, method, operation, baseUrl))
    })
  })

  const project = {
    id: generateId(),
    type: 'project',
    name: projectName,
    description: projectDescription,
    children: children
  }

  // 保留原始文档的 components（可复用定义），以便存储与重新导出时不丢失
  if (doc.components && typeof doc.components === 'object') {
    project.openapiComponents = doc.components
  }

  return [project]
}

// OpenAPI operation → 内部 API
function convertOperationToApi(path, method, operation, baseUrl) {
  const summary = operation.summary || `${method.toUpperCase()} ${path}`
  const url = buildUrl(baseUrl, path)

  return {
    id: generateId(),
    type: 'api',
    name: summary,
    method: method.toUpperCase(),
    url: url,
    headers: parseOpenApiHeaders(operation.parameters),
    params: parseOpenApiParams(operation.parameters),
    body: parseOpenApiRequestBody(operation.requestBody),
    responseParams: parseOpenApiResponseParams(operation.responses),
    responseHeaders: parseOpenApiResponseHeaders(operation.responses),
    responseBody: parseOpenApiResponseBody(operation.responses),
    description: operation.description || ''
  }
}

// 构建 URL（合并 baseUrl 与 path）
function buildUrl(baseUrl, path) {
  if (!baseUrl) return path || ''
  return baseUrl.replace(/\/$/, '') + (path || '')
}

// OpenAPI parameters → headers
function parseOpenApiHeaders(parameters) {
  if (!Array.isArray(parameters)) {
    return [{ key: '', value: '', description: '' }]
  }
  const headers = parameters
    .filter(p => p.in === 'header')
    .map(p => ({
      key: p.name,
      value: (p.schema && p.schema.example) || '',
      description: p.description || ''
    }))
  return headers.length > 0 ? headers : [{ key: '', value: '', description: '' }]
}

// OpenAPI parameters → query params
function parseOpenApiParams(parameters) {
  if (!Array.isArray(parameters)) {
    return [{ key: '', value: '', type: 'string', required: false, description: '' }]
  }
  const params = parameters
    .filter(p => p.in === 'query')
    .map(p => ({
      key: p.name,
      value: (p.schema && p.schema.example) || '',
      type: unmapType(p.schema && p.schema.type),
      required: p.required || false,
      description: p.description || ''
    }))
  return params.length > 0
    ? params
    : [{ key: '', value: '', type: 'string', required: false, description: '' }]
}

// OpenAPI 类型 → 内部类型
function unmapType(type) {
  if (!type) return 'string'
  return type
}

// OpenAPI requestBody → 内部 body
function parseOpenApiRequestBody(requestBody) {
  if (!requestBody || !requestBody.content) {
    return { type: 'none', content: '', formData: [] }
  }

  const content = requestBody.content

  if (content['application/json']) {
    const mediaType = content['application/json']
    const example = mediaType.example
    let contentStr = ''
    if (typeof example === 'string') {
      contentStr = example
    } else if (example !== undefined) {
      contentStr = JSON.stringify(example, null, 2)
    } else if (mediaType.schema) {
      contentStr = JSON.stringify(mediaType.schema, null, 2)
    }
    return { type: 'json', content: contentStr, formData: [] }
  }

  if (content['multipart/form-data']) {
    const properties = (content['multipart/form-data'].schema || {}).properties || {}
    const formData = Object.keys(properties).map(key => ({
      key,
      value: properties[key].example || '',
      type: properties[key].format === 'binary' ? 'file' : 'text',
      description: properties[key].description || ''
    }))
    return { type: 'form', content: '', formData }
  }

  if (content['application/x-www-form-urlencoded']) {
    const properties = (content['application/x-www-form-urlencoded'].schema || {}).properties || {}
    const formData = Object.keys(properties).map(key => ({
      key,
      value: properties[key].example || '',
      type: 'text',
      description: properties[key].description || ''
    }))
    return { type: 'x-www-form-urlencoded', content: '', formData }
  }

  if (content['text/plain']) {
    return { type: 'raw', content: content['text/plain'].example || '', formData: [] }
  }

  if (content['application/octet-stream']) {
    return { type: 'binary', content: '', formData: [], file: null, fileName: '' }
  }

  return { type: 'none', content: '', formData: [] }
}

// OpenAPI responses → 响应参数
function parseOpenApiResponseParams(responses) {
  if (!responses) return []
  const response = responses['200'] || responses['201'] || Object.values(responses)[0]
  const schema =
    response &&
    response.content &&
    response.content['application/json'] &&
    response.content['application/json'].schema
  if (schema && schema.properties) {
    return Object.keys(schema.properties).map(key => ({
      key,
      type: unmapType(schema.properties[key].type),
      required: false,
      value: schema.properties[key].example || '',
      description: schema.properties[key].description || ''
    }))
  }
  return []
}

// OpenAPI responses → 响应 headers
function parseOpenApiResponseHeaders(responses) {
  if (!responses) return []
  const response = responses['200'] || responses['201'] || Object.values(responses)[0]
  if (response && response.headers) {
    return Object.keys(response.headers).map(key => ({
      key,
      value: response.headers[key].example || '',
      description: response.headers[key].description || ''
    }))
  }
  return []
}

// OpenAPI responses → 响应 body
function parseOpenApiResponseBody(responses) {
  if (!responses) return { type: 'none', content: '' }
  const response = responses['200'] || responses['201'] || Object.values(responses)[0]
  if (!response || !response.content) return { type: 'none', content: '' }

  if (response.content['application/json']) {
    const example = response.content['application/json'].example
    let contentStr = ''
    if (typeof example === 'string') {
      contentStr = example
    } else if (example !== undefined) {
      contentStr = JSON.stringify(example, null, 2)
    }
    return { type: 'json', content: contentStr }
  }
  if (response.content['application/xml']) {
    return { type: 'xml', content: response.content['application/xml'].example || '' }
  }
  if (response.content['text/plain']) {
    return { type: 'raw', content: response.content['text/plain'].example || '' }
  }
  return { type: 'none', content: '' }
}

// 生成唯一 ID
function generateId() {
  return (
    Math.random().toString(36).substring(2, 15) +
    Math.random().toString(36).substring(2, 15)
  )
}

// 导出为文件
export function downloadOpenApiFile(data, filename) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

// 从文件导入
export function importOpenApiFromFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = e => {
      try {
        const data = JSON.parse(e.target.result)
        resolve(data)
      } catch (error) {
        reject(new Error('无效的 JSON 文件'))
      }
    }
    reader.onerror = () => reject(new Error('读取文件失败'))
    reader.readAsText(file)
  })
}
